import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { ObservationDraftScopeContext } from '../components/ObservationDraftScope';
import {
  ObservationDraftKeyProps,
  observationDraftService,
} from '../services/observationDraftService';

export type ObservationFieldBinding =
  | { mode: 'legacy' }
  | {
      mode: 'draft';
      /** Current value ('' when unset). Updates instantly on setValue and survives remounts. */
      value: string;
      /** True once the persisted value has been read (or the user already typed). */
      ready: boolean;
      /** True when this field has been deliberately removed (tombstoned) - distinct from
       * never-set. Lets a consumer suppress any defaultValue seed after a removal. */
      isRemoved: boolean;
      setValue: (value: string) => void;
      /** Tombstone this field's observation (validation-reject / uncheck semantics). */
      remove: () => void;
      /** Tombstone a RELATED path for the same entity (checkbox child paths). */
      removePath: (path: string) => void;
    };

/**
 * Binds a self-persisting field atom to the observation draft layer when the screen
 * is wrapped in ObservationDraftScope; returns { mode: 'legacy' } otherwise so the
 * atom keeps its original behaviour byte-for-byte. Collection observables are
 * detected during seeding and forced back to legacy mode.
 */
export function useObservationField(
  props: ObservationDraftKeyProps,
): ObservationFieldBinding {
  const scope = useContext(ObservationDraftScopeContext);
  const inScope = scope !== null && scope.inspectionId === props.inspectionId;

  const key = inScope ? observationDraftService.keyOf(props) : '';

  const subscribe = useCallback(
    (listener: () => void) =>
      inScope
        ? observationDraftService.subscribe(key, listener)
        : () => undefined,
    [key, inScope],
  );
  const snapshot = useSyncExternalStore(subscribe, () =>
    inScope ? observationDraftService.getSnapshot(key) : undefined,
  );

  useEffect(() => {
    if (inScope) {
      void observationDraftService.ensureSeeded(props);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, inScope]);

  return useMemo<ObservationFieldBinding>(() => {
    if (!inScope || snapshot?.passthrough) {
      return { mode: 'legacy' };
    }
    return {
      mode: 'draft',
      value: snapshot?.value ?? '',
      ready: !!snapshot?.seeded,
      isRemoved: snapshot?.value === null, // tombstone: sticks across the flush (value stays null)
      setValue: (value: string) =>
        observationDraftService.setValue(props, value),
      remove: () => observationDraftService.markDeleted(props),
      removePath: (path: string) =>
        observationDraftService.markDeleted({ ...props, path }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inScope, key, snapshot]);
}
